import { useEffect, useState } from 'react'
import { getStaff, supabase } from '@unvld/db'
import type { Staff, StaffRole } from '@unvld/db'
import { useAuth } from '../stores/auth'
import { toast } from 'sonner'
import { UserPlus, MoreVertical, Shield, Eye, Beer, DoorOpen, Crown, Trash2 } from 'lucide-react'
import { Modal } from '@unvld/ui'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

const ROLE_CONFIG: Record<StaffRole, { label: string; color: string; bg: string }> = {
  owner: { label: 'Inhaber', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  manager: { label: 'Manager', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  door: { label: 'Tür', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  bar: { label: 'Bar', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  readonly: { label: 'Lesezugriff', color: 'text-slate-400', bg: 'bg-slate-500/10' },
}

const INVITE_ROLES: { value: StaffRole; label: string }[] = [
  { value: 'manager', label: 'Manager' },
  { value: 'door', label: 'Tür' },
  { value: 'bar', label: 'Bar' },
  { value: 'readonly', label: 'Lesezugriff' },
]

const PERMISSION_LABELS: Record<string, string> = {
  events: 'Events verwalten',
  guests: 'Gäste einsehen',
  payments: 'Zahlungen einsehen',
  settings: 'Einstellungen ändern',
  staff: 'Mitarbeiter verwalten',
  analytics: 'Analytics einsehen',
}

export function StaffPage() {
  const { staff: currentStaff } = useAuth()
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [actionMenuId, setActionMenuId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState<Staff | null>(null)

  useEffect(() => {
    loadStaff()
  }, [])

  const loadStaff = async () => {
    setLoading(true)
    try {
      const data = await getStaff()
      setStaffList(data)
    } catch (err: any) {
      toast.error('Mitarbeiter konnten nicht geladen werden')
    }
    setLoading(false)
  }

  const toggleActive = async (member: Staff) => {
    try {
      const { error } = await supabase
        .from('staff')
        .update({ active: !member.active })
        .eq('id', member.id)
      if (error) throw error
      toast.success(member.active ? 'Mitarbeiter deaktiviert' : 'Mitarbeiter aktiviert')
      loadStaff()
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Aktualisieren')
    }
    setActionMenuId(null)
  }

  const confirmDeleteMember = (member: Staff) => {
    if (member.role === 'owner') return
    setMemberToDelete(member)
    setShowDeleteModal(true)
    setActionMenuId(null)
  }

  const deleteMember = async () => {
    if (!memberToDelete) return
    try {
      const { error } = await supabase
        .from('staff')
        .delete()
        .eq('id', memberToDelete.id)
      if (error) throw error
      toast.success('Mitarbeiter entfernt')
      loadStaff()
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Löschen')
    }
    setShowDeleteModal(false)
    setMemberToDelete(null)
  }

  if (loading && staffList.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Mitarbeiter</h1>
          <p className="text-sm text-slate-400 mt-1">{staffList.length} Mitarbeiter insgesamt</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-medium rounded-lg hover:bg-white/90 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Mitarbeiter einladen
        </button>
      </div>

      {/* Staff Table */}
      <div className="bg-[#131316] border border-white/5 rounded-xl overflow-auto max-h-[calc(100vh-12rem)]">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-[#131316]">
            <tr className="border-b border-white/5">
              <th scope="col" className="text-left px-6 py-3.5 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
              <th scope="col" className="text-left px-6 py-3.5 text-xs font-medium text-slate-400 uppercase tracking-wider">Rolle</th>
              <th scope="col" className="text-left px-6 py-3.5 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
              <th scope="col" className="text-left px-6 py-3.5 text-xs font-medium text-slate-400 uppercase tracking-wider">Letzter Login</th>
              <th scope="col" className="text-right px-6 py-3.5 text-xs font-medium text-slate-400 uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {staffList.map((member) => {
              const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.readonly
              const isCurrentUser = member.id === currentStaff?.id

              return (
                <tr key={member.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-medium text-white">
                        {member.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {member.display_name}
                          {isCurrentUser && <span className="ml-2 text-xs text-slate-500">(Du)</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleConfig.bg} ${roleConfig.color}`}>
                      {roleConfig.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {member.active ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                        Inaktiv
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-400">
                      {member.last_active_at
                        ? format(new Date(member.last_active_at), 'd. MMM yyyy, HH:mm', { locale: de })
                        : 'Nie'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {member.role !== 'owner' && !isCurrentUser && (
                      <div className="relative inline-block">
                        <button
                          onClick={() => setActionMenuId(actionMenuId === member.id ? null : member.id)}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {actionMenuId === member.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setActionMenuId(null)} />
                            <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-[#1c1c20] border border-white/10 rounded-lg shadow-xl overflow-hidden">
                              <button
                                onClick={() => toggleActive(member)}
                                className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors"
                              >
                                {member.active ? 'Deaktivieren' : 'Aktivieren'}
                              </button>
                              <button
                                onClick={() => confirmDeleteMember(member)}
                                className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors"
                              >
                                Entfernen
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {staffList.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Noch keine Mitarbeiter vorhanden
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setMemberToDelete(null) }}
        title="Mitarbeiter entfernen"
        description="Diese Aktion kann nicht rückgängig gemacht werden."
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            {memberToDelete?.display_name} wirklich entfernen?
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={() => { setShowDeleteModal(false); setMemberToDelete(null) }}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={deleteMember}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Entfernen
            </button>
          </div>
        </div>
      </Modal>

      {/* Invite Modal */}
      <InviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false)
          loadStaff()
        }}
      />
    </div>
  )
}

function InviteModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<StaffRole>('door')
  const [permissions, setPermissions] = useState({
    events: true,
    guests: true,
    payments: false,
    settings: false,
    staff: false,
    analytics: true,
  })
  const [sending, setSending] = useState(false)

  const togglePermission = (key: string) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))
  }

  const handleInvite = async () => {
    if (!email.trim() || !displayName.trim()) {
      toast.error('Bitte alle Felder ausfüllen')
      return
    }

    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-invite-staff`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          display_name: displayName.trim(),
          role,
          permissions: role === 'manager' ? permissions : undefined,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Einladung fehlgeschlagen')

      toast.success(`Einladung an ${email} gesendet`)
      onSuccess()
    } catch (err: any) {
      toast.error(err.message || 'Einladung fehlgeschlagen')
    }
    setSending(false)
  }

  return (
    <Modal open={open} onClose={onClose} title="Mitarbeiter einladen">
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1.5">E-Mail-Adresse</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="mitarbeiter@club.de"
            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Anzeigename</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Max Mustermann"
            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Rolle</label>
          <div className="grid grid-cols-2 gap-2">
            {INVITE_ROLES.map((r) => {
              const config = ROLE_CONFIG[r.value]
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border ${
                    role === r.value
                      ? `${config.bg} ${config.color} border-current`
                      : 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {r.label}
                </button>
              )
            })}
          </div>
        </div>

        {role === 'manager' && (
          <div>
            <label className="block text-sm text-slate-400 mb-2">Berechtigungen</label>
            <div className="space-y-2">
              {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between px-3 py-2 bg-white/[0.02] border border-white/5 rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors">
                  <span className="text-sm text-white">{label}</span>
                  <button
                    type="button"
                    onClick={() => togglePermission(key)}
                    className={`relative w-10 h-5.5 rounded-full transition-colors ${
                      permissions[key as keyof typeof permissions] ? 'bg-indigo-600' : 'bg-white/10'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white transition-transform shadow-sm ${
                        permissions[key as keyof typeof permissions] ? 'translate-x-[18px]' : 'translate-x-0'
                      }`}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </button>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm text-slate-400 hover:text-white transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleInvite}
            disabled={sending || !email.trim() || !displayName.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium rounded-lg hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            Einladen
          </button>
        </div>
      </div>
    </Modal>
  )
}
